import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LocationFields, managedLocations } from '../../auth/managedLocations';
import { ReportFlags, Submission } from './alertRules';
import { LoadedClient, toDashboardClient } from './clientRecord';
import { deriveClientTasks, sortTasks, sortWork } from './deriveTasks';
import {
  ClearedReasons,
  fetchClearedReasons,
  fetchCompletedCheckIns,
  fetchLatestSuccess,
  fetchReportFlags,
  fetchScopedClients,
  fetchSubmissions,
  mapWithLimit,
  needsProgrammeDocuments,
} from './taskSources';
import { ClientWork, DashboardClient, Programme, SourceKey, SourceStatuses, TaskScope, ViewerRole } from './types';

export { sortWork };

/** Per-client reads in flight at once; enough to be quick without flooding Firestore. */
const DOC_CONCURRENCY = 8;
/** A load this recent is shown at once on return, while a fresh one runs behind it. */
export const TASK_CACHE_MS = 5 * 60 * 1000;

interface Snapshot {
  clients: LoadedClient[];
  completedCheckIns?: Set<string>;
  latestSuccess?: Map<string, Date>;
  /** null: the cleared lists failed to load. */
  cleared?: Map<string, ClearedReasons> | null;
  reports: Map<string, ReportFlags>;
  submissions: Map<string, Submission[]>;
  sources: SourceStatuses;
  /** Clients a per-client source could not be read for. */
  failures: Partial<Record<SourceKey, number>>;
  loadedAt: Date;
}

/** Writes made from this tab, applied on top of every read so a reload that raced them can't bring a task back. */
interface Overrides {
  cleared: Map<string, ClearedReasons>;
  checkIns: Set<string>;
}

const LOADING: SourceStatuses = { checkIns: 'loading', success: 'loading', cleared: 'loading', reports: 'loading', submissions: 'loading' };
const cache = new Map<string, Snapshot>();
const overrides = new Map<string, Overrides>();

/** For tests. */
export const clearTaskCache = () => {
  cache.clear();
  overrides.clear();
};

const overridesFor = (scopeKey: string) => {
  let entry = overrides.get(scopeKey);
  if (!entry) {
    entry = { cleared: new Map(), checkIns: new Set() };
    overrides.set(scopeKey, entry);
  }
  return entry;
};

const isSettled = (sources: SourceStatuses) => Object.values(sources).every((s) => s !== 'loading');

/** The task scope a signed-in user reads: admins everything, managers their locations, coaches their own clients. */
export function scopeForViewer(role: string | null | undefined, metadata?: (LocationFields & { coach_id?: string }) | null): TaskScope | null {
  if (role === 'ADMIN') return { kind: 'all' };
  const locations = managedLocations(metadata);
  if (role === 'MANAGER' && locations.length > 0) return { kind: 'location', locations };
  if (role === 'USER' && metadata?.coach_id) return { kind: 'coach', coachId: String(metadata.coach_id) };
  return null;
}

export const isViewerRole = (role: string | null | undefined): role is ViewerRole => role === 'ADMIN' || role === 'MANAGER' || role === 'USER';

export interface ClientTasksResult {
  /** The client list itself; tasks fill in as their sources arrive. */
  status: 'idle' | 'loading' | 'ready' | 'error';
  /** Set when the client list failed: with status 'error' on a first load, or with the previous data still shown after a refresh. */
  error: string | null;
  /** A newer load is running behind the data on screen. */
  refreshing: boolean;
  clients: DashboardClient[];
  /** Clients with at least one open task, most urgent first. */
  work: ClientWork[];
  sources: SourceStatuses;
  failures: Partial<Record<SourceKey, number>>;
  loadedAt: Date | null;
  refresh: () => void;
  /** Reflects a check-in just logged, without a reload. */
  markCheckInLogged: (clientId: string, type: '2_week' | '10_week') => void;
  /** Reflects an alert just cleared, without a reload. Safe to call after the caller unmounts. */
  markAlertCleared: (clientId: string, programme: Programme, reason: string) => void;
}

/**
 * Every open task for the clients in `scope`, grouped by client.
 *
 * A first load is progressive: the client list arrives first (so a schedule
 * can render), then check-ins, success records, cleared alerts and the
 * per-client report and assessment reads, each with its own status so a
 * failed read shows as missing information rather than an all-clear. A recent
 * load is shown straight away on return while a fresh one runs behind it and
 * replaces it only once complete, so nothing flickers.
 */
export function useClientTasks(scope: TaskScope | null): ClientTasksResult {
  const scopeKey = scope ? JSON.stringify(scope) : null;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const freshCache = (key: string | null) => {
    const entry = key ? cache.get(key) : undefined;
    return entry && Date.now() - entry.loadedAt.getTime() < TASK_CACHE_MS ? entry : null;
  };
  const [snapshot, setSnapshot] = useState<Snapshot | null>(() => freshCache(scopeKey));
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const [status, setStatus] = useState<ClientTasksResult['status']>(snapshot ? 'ready' : scopeKey ? 'loading' : 'idle');
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [overrideVersion, setOverrideVersion] = useState(0);
  const forceRef = useRef(false);

  useEffect(() => {
    const current = scopeRef.current;
    if (!scopeKey || !current) {
      setSnapshot(null);
      setStatus('idle');
      setRefreshing(false);
      return;
    }
    const forced = forceRef.current;
    forceRef.current = false;
    // What stays on screen while this load runs: the current data on a refresh, a recent load on return, else nothing.
    const shown = forced ? snapshotRef.current : freshCache(scopeKey);
    if (!forced) setSnapshot(shown);
    const background = shown !== null;

    let cancelled = false;
    let draft: Snapshot | null = null;
    const publish = (next: Snapshot) => {
      const done = isSettled(next.sources);
      if (done) cache.set(scopeKey, next);
      if (!background || done) setSnapshot(next);
      if (done) setRefreshing(false);
    };
    const update = (change: (prev: Snapshot) => Snapshot) => {
      if (cancelled || !draft) return;
      draft = change(draft);
      publish(draft);
    };
    const settle = (key: SourceKey, ok: boolean, patch: Partial<Snapshot> = {}) =>
      update((prev) => ({ ...prev, ...patch, sources: { ...prev.sources, [key]: ok ? 'ready' : 'error' } }));

    setError(null);
    setRefreshing(background);
    if (!background) setStatus('loading');
    fetchScopedClients(current)
      .then((clients) => {
        if (cancelled) return;
        draft = { clients, reports: new Map(), submissions: new Map(), sources: { ...LOADING }, failures: {}, loadedAt: new Date() };
        publish(draft);
        setStatus('ready');

        fetchCompletedCheckIns(current, clients).then(
          (completedCheckIns) => settle('checkIns', true, { completedCheckIns }),
          () => settle('checkIns', false)
        );
        fetchLatestSuccess(current, clients).then(
          (latestSuccess) => settle('success', true, { latestSuccess }),
          () => settle('success', false)
        );
        fetchClearedReasons(current, clients).then(
          (cleared) => settle('cleared', true, { cleared }),
          () => settle('cleared', false, { cleared: null })
        );

        const programmeClients = clients.filter((client) => needsProgrammeDocuments(client.raw));
        const reports = new Map<string, ReportFlags>();
        const submissions = new Map<string, Submission[]>();
        let reportFailures = 0;
        let submissionFailures = 0;
        mapWithLimit(programmeClients, DOC_CONCURRENCY, async (client) => {
          const [flags, rows] = await Promise.allSettled([fetchReportFlags(client.clientId), fetchSubmissions(client.clientId)]);
          if (flags.status === 'fulfilled') reports.set(client.clientId, flags.value);
          else reportFailures += 1;
          if (rows.status === 'fulfilled') submissions.set(client.clientId, rows.value);
          else submissionFailures += 1;
        }).then(() =>
          update((prev) => ({
            ...prev,
            reports,
            submissions,
            sources: { ...prev.sources, reports: reportFailures ? 'error' : 'ready', submissions: submissionFailures ? 'error' : 'ready' },
            failures: { ...prev.failures, reports: reportFailures || undefined, submissions: submissionFailures || undefined },
          }))
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Dashboard: loading clients failed', err);
        setRefreshing(false);
        if (background) {
          setError('Could not refresh. Showing the last loaded data.');
        } else {
          setError('Clients could not be loaded. Check your connection and try again.');
          setStatus('error');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [scopeKey, attempt]);

  const refresh = useCallback(() => {
    forceRef.current = true;
    setAttempt((n) => n + 1);
  }, []);

  const markCheckInLogged = useCallback(
    (clientId: string, type: '2_week' | '10_week') => {
      if (!scopeKey) return;
      overridesFor(scopeKey).checkIns.add(`${clientId}_${type}`);
      setOverrideVersion((n) => n + 1);
    },
    [scopeKey]
  );

  const markAlertCleared = useCallback(
    (clientId: string, programme: Programme, reason: string) => {
      if (!scopeKey) return;
      const store = overridesFor(scopeKey).cleared;
      const entry = store.get(clientId) ?? { ignition: new Set<string>(), foundation: new Set<string>() };
      entry[programme].add(reason);
      store.set(clientId, entry);
      setOverrideVersion((n) => n + 1);
    },
    [scopeKey]
  );

  const derived = useMemo(() => {
    if (!snapshot || !scopeKey) return { clients: [] as DashboardClient[], work: [] as ClientWork[] };
    const local = overrides.get(scopeKey);
    const now = new Date();
    const clients: DashboardClient[] = [];
    const work: ClientWork[] = [];
    // A source still loading stays unknown: local writes are layered onto loaded data, never stand in for it.
    const completedCheckIns =
      snapshot.completedCheckIns && local?.checkIns.size ? new Set([...Array.from(snapshot.completedCheckIns), ...Array.from(local.checkIns)]) : snapshot.completedCheckIns;
    snapshot.clients.forEach((loaded) => {
      const client = toDashboardClient(loaded, now);
      clients.push(client);
      const loadedCleared = snapshot.cleared ? snapshot.cleared.get(loaded.clientId) : undefined;
      const localCleared = local?.cleared.get(loaded.clientId);
      const cleared =
        snapshot.cleared === undefined
          ? undefined
          : snapshot.cleared === null && !localCleared
            ? null
            : {
                ignition: new Set([...Array.from(loadedCleared?.ignition ?? []), ...Array.from(localCleared?.ignition ?? [])]),
                foundation: new Set([...Array.from(loadedCleared?.foundation ?? []), ...Array.from(localCleared?.foundation ?? [])]),
              };
      const tasks = deriveClientTasks(
        loaded,
        {
          completedCheckIns,
          latestSuccess: snapshot.latestSuccess,
          cleared,
          reports: snapshot.reports.get(loaded.clientId),
          submissions: snapshot.submissions.get(loaded.clientId),
        },
        now
      );
      if (tasks.length) work.push({ client, tasks: sortTasks(tasks), urgency: tasks[0].urgency });
    });
    return { clients, work: sortWork(work) };
    // overrideVersion re-derives after a local write; the overrides themselves live outside React state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot, scopeKey, overrideVersion]);

  return {
    status,
    error,
    refreshing,
    clients: derived.clients,
    work: derived.work,
    sources: snapshot?.sources ?? LOADING,
    failures: snapshot?.failures ?? {},
    loadedAt: snapshot?.loadedAt ?? null,
    refresh,
    markCheckInLogged,
    markAlertCleared,
  };
}
